import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {PROCEDURE_PRODUCT_LIMITS} from 'procedures/design/ProcedureDesignConstants';
import {ProcedureEditorContext} from 'procedures/design/ProcedureEditorContext';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {GRAY_DARKER, GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import {ProcedureMethodSettingsDropdownOptions} from './utils/ProcedureMethodSettingsDropdownOptions';

interface ProcedureMethodSettingsDropdownProps {
  methodCid: string;
  onDuplicateClick: (nodeId: string) => void;
  onEditMethodSelection: (nodeId: string) => void;
  onDeleteMethod: (methodCid: string) => void;
}

export default function ProcedureMethodSettingsDropdown(
  props: ProcedureMethodSettingsDropdownProps
): JSX.Element {
  const {numberOfMethods} = ProcedureEditorContext.useContext();
  const {procedureType} = ProcedurePageContext.useContext();
  const isMethodLimitReached = numberOfMethods >= PROCEDURE_PRODUCT_LIMITS.METHODS_PER_PROCEDURE;

  return (
    <Dropdown
      closeOnInsideClick={true}
      closeOnOutsideClick={true}
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.SMALL}>
          <Option
            icon="pen"
            onSelect={() => {
              props.onEditMethodSelection(props.methodCid);
            }}
          >
            {ProcedureMethodSettingsDropdownOptions.EDIT}
          </Option>
          <Option
            icon="copy"
            onSelect={() => {
              props.onDuplicateClick(props.methodCid);
            }}
            isDisabled={isMethodLimitReached}
          >
            <Popover
              placement={FloatingPlacement.TOP}
              renderContent={() => (
                <div>
                  {`Limit of ${PROCEDURE_PRODUCT_LIMITS.METHODS_PER_PROCEDURE} ${getProcedureUIString(
                    ProceduresStringKeys.METHOD,
                    procedureType,
                    {
                      stringCase: StringCase.LOWER,
                      plural: true,
                    }
                  )} per ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
                    stringCase: StringCase.LOWER,
                  })} reached`}
                </div>
              )}
              isDisabled={!isMethodLimitReached}
            >
              <>{ProcedureMethodSettingsDropdownOptions.DUPLICATE}</>
            </Popover>
          </Option>
          <Option
            icon="trash-can"
            onSelect={() => {
              props.onDeleteMethod(props.methodCid);
            }}
          >
            {ProcedureMethodSettingsDropdownOptions.DELETE}
          </Option>
        </Menu>
      }
      alwaysDropDown={true}
    >
      <BenchlingIcon className={css(styles.settingsNodeButton)} icon="settings" />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  settingsNodeButton: {
    float: 'right',
    padding: '8px',
    width: '14px',
    height: '14px',
    borderRadius: 4,
    background: GRAY_LIGHT,
    color: GRAY_DARKER,
    ':hover': {
      color: NEAR_BLACK,
      cursor: 'pointer',
    },
  },
});
